type Badge = { tag?: string; value?: string };
type SearchResult = { badges?: Badge[]; sku?: string };
type SearchResponse = { results?: SearchResult[] };
type TagsConfig = Record<
  string,
  {
    enabled?: boolean;
    location?: string;
    priority?: number;
    component?: string;
    parameters?: { colorText?: string; color?: string; url?: string };
  }
>;
type MetaResponse = { badges?: { tags?: TagsConfig } };

const BADGES_SELECTOR = '[data-entity="ss-badges"]';
const PERCENTAGE_OFF_TAG = "percentage-off-sale";
const SPECIAL_ORDER_TAG = "special-order";
const FETCH_TIMEOUT_MS = 8000;
const UNKNOWN_LOCATION_RANK = 10;
const DEFAULT_PRIORITY = 100;

let cachedTagsConfig: TagsConfig | null | undefined;

function fetchJson<T>(url: string): Promise<T | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  return fetch(url, { credentials: "omit", signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => clearTimeout(timeoutId));
}

async function fetchBadges(siteId: string, sku: string): Promise<Badge[]> {
  const url =
    "https://api.searchspring.net/api/search/search.json?siteId=" +
    encodeURIComponent(siteId) +
    "&resultsFormat=native&resultsPerPage=10&q=" +
    encodeURIComponent(sku);
  const data = await fetchJson<SearchResponse>(url);
  const results = data?.results;
  if (!results || !results.length) return [];

  const exactMatch = results.find((result) => result.sku === sku);
  const source = exactMatch ?? results[0];

  return source.badges && Array.isArray(source.badges) ? source.badges : [];
}

async function fetchTagsConfig(siteId: string): Promise<TagsConfig | null> {
  if (cachedTagsConfig !== undefined) {
    return cachedTagsConfig;
  }

  const url =
    "https://" +
    encodeURIComponent(siteId) +
    ".a.searchspring.io/api/meta/meta?siteId=" +
    encodeURIComponent(siteId);
  const data = await fetchJson<MetaResponse>(url);

  cachedTagsConfig =
    data?.badges && typeof data.badges.tags === "object"
      ? data.badges.tags ?? null
      : null;

  return cachedTagsConfig;
}

function getLocationRank(location: string | undefined): number {
  if (location === "callout") return 0;
  if (location === "left") return 1;
  if (location === "right") return 2;
  return UNKNOWN_LOCATION_RANK;
}

function renderBadges(
  containerElement: HTMLElement,
  badges: Badge[],
  tagsConfig: TagsConfig | null
) {
  const hasPercentageOff = badges.some(
    (badge) => badge?.tag === PERCENTAGE_OFF_TAG
  );
  const badgesWithIndex = badges.map((badge, index) => ({ badge, index }));
  const filteredBadges = badgesWithIndex.filter(({ badge }) => {
    const tag = badge.tag || "";
    const value = badge.value || "";
    if (!tag && !value) return false;
    if (tag === "sale" && hasPercentageOff) return false;
    if (tag === SPECIAL_ORDER_TAG) return false;
    if (value.toLowerCase().includes("special order")) return false;
    if (!tagsConfig || !tag) return true;
    const config = tagsConfig[tag];
    if (config && config.enabled === false) return false;
    return true;
  });

  if (!filteredBadges.length) return;

  if (tagsConfig) {
    filteredBadges.sort((left, right) => {
      const leftTag = left.badge.tag || "";
      const rightTag = right.badge.tag || "";
      const leftConfig = leftTag ? tagsConfig[leftTag] : undefined;
      const rightConfig = rightTag ? tagsConfig[rightTag] : undefined;

      const leftLocationRank = getLocationRank(
        leftConfig?.location && leftConfig.location.split("/")[0]
      );
      const rightLocationRank = getLocationRank(
        rightConfig?.location && rightConfig.location.split("/")[0]
      );
      if (leftLocationRank !== rightLocationRank) {
        return leftLocationRank - rightLocationRank;
      }

      const leftPriority =
        typeof leftConfig?.priority === "number"
          ? leftConfig.priority
          : DEFAULT_PRIORITY;
      const rightPriority =
        typeof rightConfig?.priority === "number"
          ? rightConfig.priority
          : DEFAULT_PRIORITY;
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return left.index - right.index;
    });
  }

  const fragment = document.createDocumentFragment();

  for (const { badge } of filteredBadges) {
    const tag = badge.tag || "";
    const value = badge.value || "";

    const badgeWrapper = document.createElement("div");
    const wrapperClasses = ["card__badge", "top"];

    if (tagsConfig && tag) {
      const config = tagsConfig[tag];
      const location = config?.location;
      if (location) {
        const locationKey = location.split("/")[0];
        if (locationKey === "right") wrapperClasses.push("right");
      }
    }

    badgeWrapper.className = wrapperClasses.join(" ");

    const badgeLabel = document.createElement("span");
    badgeLabel.className = tagsConfig ? "ss-pdp-badge" : "ss-pdp-badge-default";

    const parameters = tagsConfig?.[tag]?.parameters;
    if (parameters?.colorText) badgeLabel.style.color = parameters.colorText;
    if (parameters?.color) badgeLabel.style.backgroundColor = parameters.color;

    const imageUrl = parameters?.url;
    if (imageUrl) {
      const image = document.createElement("img");
      image.className = "ss-pdp-badge-img";
      image.src = imageUrl;
      image.alt = value || tag;
      badgeLabel.textContent = "";
      badgeLabel.append(image);
    } else {
      badgeLabel.textContent =
        tag === PERCENTAGE_OFF_TAG && value ? `${value}` : value || tag;
    }
    badgeWrapper.append(badgeLabel);
    fragment.append(badgeWrapper);
  }

  containerElement.classList.add("card__badges");
  if (!tagsConfig) containerElement.classList.add("badges", "badges--top-left");
  containerElement.append(fragment);
}

async function mountBadges(containerElement: HTMLElement) {
  if (
    containerElement.dataset.ssBadgesBound === "true" ||
    containerElement.childElementCount
  )
    return;
  const { id: sku, siteId } = containerElement.dataset;
  if (!sku || !siteId) return;

  containerElement.dataset.ssBadgesBound = "true";
  const [badges, tagsConfig] = await Promise.all([
    fetchBadges(siteId, sku),
    fetchTagsConfig(siteId),
  ]);
  if (badges.length) renderBadges(containerElement, badges, tagsConfig);
}

function mountBadgesIn(rootNode: ParentNode) {
  rootNode.querySelectorAll(BADGES_SELECTOR).forEach((element) => {
    if (element instanceof HTMLElement) void mountBadges(element);
  });
}

if (document.readyState !== "loading") mountBadgesIn(document);
document.addEventListener("DOMContentLoaded", () => mountBadgesIn(document));

document.addEventListener("shopify:section:load", (event) => {
  const targetElement = event.target;
  if (targetElement instanceof HTMLElement) mountBadgesIn(targetElement);
});
